import {ensureGraphPalette, isDarkMode} from "../helpers/dark_mode";
import {registerGraph, unregisterGraph} from "../helpers/graph_registry";
import {
    attachTimeCrosshairSync,
    detachTimeCrosshairSync,
    invalidateTimeCrosshairAxisCache,
    refreshTimeCrosshairAccessibility,
} from "../helpers/time_crosshair_sync";

export class DashboardGraph {
    constructor(id) {
        this.id = id;
        this.dom = document.getElementById(id);
        if (!this.dom) {
            throw new Error("No graph DOM found with id '" + id + "'");
        }
        this.dom.classList.add("muted");
        ensureGraphPalette();
        this.chart = null;
        this.option = {};
        this.resizeObserver = null;
        this.resizeFrame = null;
        this.#initChart(isDarkMode() ? 'dark' : 'vintage');
        this.#bindAutoResize();
        this.chart.showLoading();
        registerGraph(this);
    }

    onThemeChange() {
        // Override this if you have to
    }

    reinitializeTheme(themeName) {
        if (!this.dom || !this.dom.isConnected) {
            this.dispose();
            return;
        }
        if (this.chart) {
            detachTimeCrosshairSync(this);
            this.chart.dispose();
        }
        this.#initChart(themeName);
        this.chart.setOption(this.option);
        this.onThemeChange();
    }

    dispose() {
        detachTimeCrosshairSync(this);
        unregisterGraph(this);
        if (this.resizeFrame !== null) {
            cancelAnimationFrame(this.resizeFrame);
            this.resizeFrame = null;
        }
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
        if (this.chart) {
            this.chart.dispose();
            this.chart = null;
        }
        this.dom = null;
    }

    #initChart(themeName) {
        this.chart = echarts.init(this.dom, themeName);
        const setOption = this.chart.setOption.bind(this.chart);
        this.chart.setOption = (...args) => {
            invalidateTimeCrosshairAxisCache(this);
            const result = setOption(...args);
            refreshTimeCrosshairAccessibility(this);
            return result;
        };
        attachTimeCrosshairSync(this);
    }

    #bindAutoResize() {
        if (typeof ResizeObserver === "undefined" || !this.dom) {
            return;
        }
        this.resizeObserver = new ResizeObserver(() => {
            if (!this.chart || !this.dom || !this.dom.isConnected) {
                return;
            }
            if (this.resizeFrame !== null) {
                cancelAnimationFrame(this.resizeFrame);
            }
            this.resizeFrame = requestAnimationFrame(() => {
                this.resizeFrame = null;
                if (!this.chart || !this.dom || !this.dom.isConnected) {
                    return;
                }
                const { width, height } = this.dom.getBoundingClientRect();
                if (width > 0 && height > 0) {
                    this.chart.resize();
                }
            });
        });
        this.resizeObserver.observe(this.dom);
    }
}
